import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Search, TrainFront } from "lucide-react";
import { AREAS, METRO_NOTE, STATES, areaPath } from "@/lib/areas";
import { BUSINESS } from "@/lib/site";
import { track } from "@/lib/analytics";

export const AreaLinkCard = ({ a, showState }) => (
  <Link to={areaPath(a)} data-testid={`area-link-${a.state}-${a.slug}`} className="group flex items-center justify-between rounded-xl border border-neutral-200 bg-white px-4 py-3 transition-[border-color,box-shadow] duration-300 hover:border-brand-ink hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
    <span>
      <span className="block text-sm font-bold">{a.name}{showState && a.state !== "washington-dc" ? `, ${STATES[a.state].short}` : ""}</span>
      <span className="block text-[11px] font-semibold text-neutral-500">~{a.mi} mi away</span>
    </span>
    <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);

export const AreaSearch = ({ areas = AREAS, label = "Search all areas" }) => {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const list = useMemo(() => (term ? areas.filter((a) => a.name.toLowerCase().includes(term)).slice(0, 12) : []), [term, areas]);
  return (
    <div data-testid="area-search">
      <div className="flex max-w-xl items-center gap-3 rounded-full border border-neutral-300 bg-white px-5 focus-within:border-brand-ink">
        <Search className="h-4 w-4 text-neutral-500" aria-hidden="true" />
        <label htmlFor="area-search" className="sr-only">{label}</label>
        <input id="area-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type your neighborhood or town" className="h-12 w-full bg-transparent placeholder:text-neutral-400 focus:outline-none" data-testid="area-search-input" />
      </div>
      {term && (
        list.length ? (
          <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="area-search-results">
            {list.map((a) => <li key={`${a.state}-${a.slug}`}><AreaLinkCard a={a} showState /></li>)}
          </ul>
        ) : (
          <p className="mt-4 rounded-2xl bg-brand-sand p-5 text-sm text-neutral-600" data-testid="area-empty-state">
            No area matches "{q}". Anyone can visit us at {BUSINESS.addressLine} — call <a href={BUSINESS.phoneHref} className="font-bold">{BUSINESS.phone}</a> for directions.
          </p>
        )
      )}
    </div>
  );
};

export const GettingHere = () => (
  <div className="grid gap-4 md:grid-cols-3" data-testid="getting-here">
    {[
      [MapPin, "Address", `${BUSINESS.addressLine}. Upstairs, next to Chicken + Whiskey — first door on the left, then ring the bell.`],
      [TrainFront, "By Metro", METRO_NOTE],
      [ArrowRight, "On foot or rideshare", "We're on 14th Street NW between R and S Streets. Set your destination to 1738 14th St NW and look for Chicken + Whiskey."],
    ].map(([Icon, t, d]) => (
      <div key={t} className="rounded-2xl border border-neutral-200 bg-white p-6">
        <Icon className="h-5 w-5" aria-hidden="true" />
        <h3 className="mt-4 font-display text-lg font-bold">{t}</h3>
        <p className="mt-2 text-sm leading-relaxed text-neutral-600">{d}</p>
      </div>
    ))}
  </div>
);

export const AreaCta = ({ where }) => (
  <section className="bg-brand-lime">
    <div className="mx-auto flex max-w-7xl flex-col justify-between gap-6 px-4 py-14 sm:px-6 md:flex-row md:items-center">
      <h2 className="max-w-xl font-display text-3xl font-black tracking-tight">Order ahead, then come upstairs to pick up.</h2>
      <div className="flex flex-wrap gap-3">
        <Link to="/shop-all" onClick={() => track("shop_now_click", { location: where })} data-testid={`${where}-shop-button`} className="btn-dark">Shop now</Link>
        <a href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer" onClick={() => track("directions_click", { location: where })} data-testid={`${where}-directions-button`} className="btn-outline">Get directions</a>
      </div>
    </div>
  </section>
);
